import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";
import TenderingDetailContext from "../../context";
import { addDays, format } from "date-fns";
import {
  formatFileSize,
  formatNumber,
} from "../../../../helpers/common.helper";
import { ReactComponent as AddDocumentSvg } from "../../../../assets/tendering/add-document.svg";
import { ReactComponent as AddImageSvg } from "../../../../assets/tendering/add-image.svg";
import { ReactComponent as DownloadSvg } from "../../../../assets/tendering/download.svg";
import { ReactComponent as TrashSvg } from "../../../../assets/admin/icons/trash.svg";
import { Button } from "../../../../components";
import AppContext from "../../../../AppContext";
import useTendering from "../../../../hooks/useTendering";

const ITEM_STATUS = {
  DRAFT: "draft",
  IDLE: "idle",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
};

const ItemWorks = ({
  termNo,
  index,
  data: _data,
  view,
  isReviewing,
  hasRejections,
  isApproved,
  previousApproved,
  onChange,
}) => {
  const {
    details: { _id: tenderingId },
    projectState: { isOwner },
  } = useContext(TenderingDetailContext);
  const [_termNo, setTermNo] = useState(undefined);
  const { patchSubmissionWorkItem } = useTendering();
  const { displayImages } = useContext(AppContext);
  const [data, setData] = useState(_data);
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [remarks, setRemarks] = useState(undefined);
  const [status, setStatus] = useState(
    view ? ITEM_STATUS.IDLE : ITEM_STATUS.DRAFT
  );

  const refetch = () => {
    setTermNo(termNo);
    setData(_data);
    setImages(_data.images ?? []);
    setDocuments(_data.documents ?? []);
    setRemarks(_data?.remark);

    if (_data.status === "accepted") {
      setStatus(ITEM_STATUS.ACCEPTED);
      return;
    }

    if (_data.status === "rejected") {
      setStatus(ITEM_STATUS.REJECTED);
      return;
    }
  };

  useEffect(() => {
    if (_termNo === termNo) return;

    refetch();
  }, [termNo, data]);

  useEffect(() => {
    if (data.status === _data.status) {
      return;
    }

    if (_data.status === "accepted") {
      setStatus(ITEM_STATUS.ACCEPTED);
      return;
    }

    if (_data.status === "rejected") {
      setStatus(ITEM_STATUS.REJECTED);
      return;
    }

    if (view) {
      setStatus(ITEM_STATUS.IDLE);
      return;
    }

    setStatus(ITEM_STATUS.DRAFT);
  }, [_data, view]);

  useEffect(() => {
    onChange({ images, documents, remark: remarks });
  }, [images, documents, remarks]);

  const _renderStatus = () => {
    let content = "To Review";

    if (status === ITEM_STATUS.DRAFT) content = "Waiting submission";
    if (status === ITEM_STATUS.ACCEPTED) content = "Accepted";
    if (status === ITEM_STATUS.REJECTED) content = "Rejected";

    return (
      <div
        className="tendering-documents__payment-terms__table__row__status"
        data-status={status}
      >
        {content}
      </div>
    );
  };

  const handleImageUpload = ({ target: { files } }) => {
    setImages((curr) => {
      const _files = [];

      for (const file of files) {
        if (curr.find((image) => image.file.name === file.name)) {
          continue;
        }

        _files.push({ file });
      }

      return curr.concat(_files);
    });
  };

  const handleDocumentUpload = ({ target: { files } }) => {
    setDocuments((curr) => {
      const _files = [];

      for (const file of files) {
        if (curr.find((doc) => doc.file.name === file.name)) {
          continue;
        }

        _files.push({ file });
      }

      return curr.concat(_files);
    });
  };

  const handleItemConfirmClick = async (accept) => {
    if (accept) {
      await patchSubmissionWorkItem(
        tenderingId,
        data.termNo,
        data.subject,
        data.item._id,
        {
          status: "accepted",
        }
      );

      setStatus(ITEM_STATUS.ACCEPTED);
      return;
    }

    // TODO: Add Modal for rejections
    await patchSubmissionWorkItem(
      tenderingId,
      data.termNo,
      data.subject,
      data.item._id,
      {
        status: "rejected",
        rejections: [
          {
            comment: "",
          },
        ],
      }
    );
    setStatus(ITEM_STATUS.REJECTED);
  };

  const renderCotractorActions = () => {
    if (!previousApproved && isApproved) return <td />;
    if (previousApproved && isApproved) return <></>;

    if (status !== ITEM_STATUS.ACCEPTED && status !== ITEM_STATUS.IDLE) {
      return (
        <td>
          <div className="tendering-documents__payment-terms__table__row__actions">
            <label
              className="tendering-documents__payment-terms__table__row__actions__action"
              for={`${index}-${data.item}-images`}
            >
              <AddImageSvg width={20} />
              <input
                type="file"
                multiple={true}
                id={`${index}-${data.item}-images`}
                onChange={handleImageUpload}
                onClick={(e) => {
                  e.target.value = null;
                }}
              />
            </label>
            <label
              className="tendering-documents__payment-terms__table__row__actions__action"
              for={`${index}-${data.item}-docs`}
            >
              <AddDocumentSvg width={20} />
              <input
                type="file"
                multiple={true}
                id={`${index}-${data.item}-docs`}
                onChange={handleDocumentUpload}
                onClick={(e) => {
                  e.target.value = null;
                }}
              />
            </label>
          </div>
        </td>
      );
    }

    return <></>;
  };

  const contractorDisplayEmptyActions =
    !isOwner && view && hasRejections && status !== ITEM_STATUS.REJECTED;

  let canUpdateFiles = false;

  if (!isOwner && (!view || (view && status === ITEM_STATUS.REJECTED))) {
    canUpdateFiles = true;
  }

  if (!data) return <></>;

  return (
    <>
      <tr className="tendering-documents__payment-terms__table__row">
        <td>{data.item.name}</td>
        <td>{format(addDays(new Date(), data.days ?? 0), "dd MMM yyyy")}</td>
        <td className="tendering-documents__payment-terms__table__row__percentage">
          {data.percentage}%
        </td>
        <td className="tendering-documents__payment-terms__table__row__total">
          {formatNumber(data.total, "")}
        </td>
        <td>
          {isOwner === true ? (
            remarks
          ) : view ? (
            remarks
          ) : (
            <textarea
              className="tendering-documents__payment-terms__table__row__remarks"
              value={remarks}
              onChange={({ target: { value } }) => {
                setRemarks(value);
              }}
            />
          )}
        </td>
        {isOwner && <td>{_renderStatus()}</td>}
        {!isOwner && view && <td>{_renderStatus()}</td>}
        {contractorDisplayEmptyActions && <td />}
        {!isOwner && renderCotractorActions()}
        {isOwner && (
          <td>
            <div className="tendering-documents__payment-terms__table__row__work__actions">
              <Button
                label="Reject"
                variant="secondary"
                size="small"
                onClick={() => handleItemConfirmClick(false)}
                disabled={status !== ITEM_STATUS.IDLE}
              />
              <Button
                label="Accept"
                size="small"
                onClick={() => handleItemConfirmClick(true)}
                disabled={status !== ITEM_STATUS.IDLE}
              />
            </div>
          </td>
        )}
      </tr>
      {((isOwner && view) || !isOwner) &&
        (images.length > 0 || documents.length > 0) && (
          <tr className="tendering-documents__payment-terms__table__row">
            <td colSpan={"100%"}>
              <div className="tendering-documents__payment-terms__table__row__work">
                {documents.length > 0 && (
                  <>
                    <div className="tendering-documents__payment-terms__table__row__work__heading">
                      <h4>Documents</h4>
                      {!canUpdateFiles && (
                        <DownloadSvg className="tendering-documents__payment-terms__table__row__work__download" />
                      )}
                    </div>
                    <div className="tendering-documents__payment-terms__table__row__work__docs">
                      {documents.map(({ file }, i) => (
                        <div
                          className="tendering-documents__payment-terms__table__row__work__docs__doc"
                          key={`document-${i}`}
                        >
                          <span className="tendering-documents__payment-terms__table__row__work__docs__doc__name">
                            {file.name}
                          </span>
                          <span className="tendering-documents__payment-terms__table__row__work__docs__doc__size">
                            {formatFileSize(file.size)}
                          </span>
                          {canUpdateFiles && (
                            <div className="tendering-documents__payment-terms__table__row__work__docs__doc__actions">
                              <div
                                className="tendering-documents__payment-terms__table__row__work__docs__doc__actions__action"
                                onClick={() => {
                                  const _files = _.cloneDeep(documents);
                                  _files.splice(i, 1);
                                  setDocuments(_files);
                                }}
                              >
                                Delete
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {images.length > 0 && (
                  <>
                    <h4>Images</h4>
                    <div className="tendering-documents__payment-terms__table__row__work__images-scroller">
                      <div className="tendering-documents__payment-terms__table__row__work__images">
                        {images.map(({ dataUrl, file }, i) => (
                          <div
                            className="tendering-documents__payment-terms__table__row__work__images__preview"
                            key={`image-${i}`}
                          >
                            <img
                              className="tendering-documents__payment-terms__table__row__work__images__preview__image"
                              src={
                                dataUrl ? dataUrl : URL.createObjectURL(file)
                              }
                              onClick={() => {
                                if (!isOwner && !view) return;

                                displayImages(
                                  images.map(
                                    (img) =>
                                      img.dataUrl ?? URL.createObjectURL(file)
                                  ),
                                  i
                                );
                              }}
                            />
                            {canUpdateFiles && (
                              <div
                                className="tendering-documents__payment-terms__table__row__work__images__preview__trash"
                                onClick={() => {
                                  const _images = _.cloneDeep(images);
                                  _images.splice(i, 1);
                                  setImages(_images);
                                }}
                              >
                                <TrashSvg width={16} />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </td>
          </tr>
        )}
    </>
  );
};

ItemWorks.propTypes = {};

export default ItemWorks;
